import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { paymentsApi } from '@/services/payments';
import { couponsApi, type CouponBenefits } from '@/services/coupons';
import { calculateMaxPerPlate, fitsOnPlate } from '@/utils/build-plate';
import { trackBeginCheckout, trackCheckoutError, stashCheckoutValue } from '@/services/analytics';

interface BoundingBox {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
}

interface CheckoutPanelProps {
  readonly fileName: string;
  readonly volumeCm3: number | null;
  readonly boundingBox: BoundingBox | null;
  readonly selectedMaterialId: string | null;
  readonly selectedMaterialName: string | null;
  readonly selectedColor: string | null;
  readonly price: number | null;
  readonly rawPrice: number | null;
  readonly estimatedDays: number | null;
  readonly quoteId: string | null;
}

const MIN_QUANTITY = 1;
const MINIMUM_ORDER_PRICE = 5.0;

export const CheckoutPanel = ({
  fileName,
  volumeCm3,
  boundingBox,
  selectedMaterialId,
  selectedMaterialName,
  selectedColor,
  price,
  rawPrice,
  estimatedDays,
  quoteId,
}: CheckoutPanelProps) => {
  const [quantity, setQuantity] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponBenefits | null>(null);

  const maxQuantity = useMemo(
    () => (boundingBox ? calculateMaxPerPlate(boundingBox) : 100),
    [boundingBox],
  );

  const modelFits = !boundingBox || fitsOnPlate(boundingBox);
  const isComplete = !!selectedMaterialId && !!selectedColor && !!price && !!quoteId;
  const canCheckout = isComplete && termsAccepted && ownershipConfirmed && modelFits;

  // The $5 minimum is a per-order baseline, not per-unit.
  // Extra copies add their actual cost; total only grows past the minimum
  // once rawPrice * quantity exceeds it.
  const effectiveRawPrice = rawPrice ?? price;
  const shouldApplyMinimum = !appliedCoupon?.waiveMinimum;
  const rawSubtotal = effectiveRawPrice
    ? shouldApplyMinimum
      ? Math.max(effectiveRawPrice * quantity, MINIMUM_ORDER_PRICE)
      : effectiveRawPrice * quantity
    : null;
  const discount = rawSubtotal && appliedCoupon?.percentOff
    ? Math.round(rawSubtotal * (appliedCoupon.percentOff / 100) * 100) / 100
    : 0;
  const subtotal = rawSubtotal
    ? Math.round((rawSubtotal - discount) * 100) / 100
    : null;
  const baselineActive = shouldApplyMinimum && effectiveRawPrice !== null && effectiveRawPrice * quantity < MINIMUM_ORDER_PRICE;

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const result = await couponsApi.validate(couponInput.trim());
      if (result.valid && result.coupon) {
        setAppliedCoupon(result.coupon);
        setCouponError(null);
      } else {
        setCouponError(result.error ?? 'Invalid coupon');
        setAppliedCoupon(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError(null);
  };

  const getButtonLabel = (): string => {
    if (!modelFits) return 'Model exceeds build volume';
    if (isLoading) return 'Redirecting to payment...';
    if (!selectedMaterialId) return 'Select a material';
    if (!selectedColor) return 'Select a color';
    if (!termsAccepted || !ownershipConfirmed) return 'Accept terms to continue';
    return 'Proceed to Checkout';
  };

  const handleCheckout = async () => {
    if (!canCheckout || !selectedMaterialId || !selectedColor || !quoteId) return;

    setIsLoading(true);
    setError(null);

    const checkoutValue = subtotal ?? 0;

    trackBeginCheckout({
      value: checkoutValue,
      checkout_type: 'quote',
      material_id: selectedMaterialId,
      color: selectedColor,
      quantity,
    });

    try {
      const { sessionUrl } = await paymentsApi.createCheckout({
        type: 'quote',
        quoteId,
        materialId: selectedMaterialId,
        color: selectedColor,
        quantity,
        couponCode: appliedCoupon?.code ?? null,
      });
      await stashCheckoutValue(checkoutValue);
      window.location.href = sessionUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      setError(message);
      trackCheckoutError({
        error_message: message,
        checkout_type: 'quote',
        value: checkoutValue,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface-sunken)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-secondary)',
          }}
        >
          Order Summary
        </h3>
      </div>

      <div
        style={{
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        {/* File */}
        <SummaryRow label="File" value={fileName} />

        {volumeCm3 && (
          <SummaryRow label="Volume" value={`${volumeCm3} cm\u00B3`} />
        )}

        {/* Material */}
        <SummaryRow
          label="Material"
          value={selectedMaterialName ?? undefined}
          placeholder="Not selected"
        />

        {/* Color */}
        <SummaryRow
          label="Color"
          value={selectedColor ? capitalize(selectedColor) : undefined}
          placeholder={selectedMaterialId ? 'Not selected' : '\u2014'}
        />

        {/* Build volume warning */}
        {!modelFits && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--bg-error-subtle)',
              padding: 'var(--space-3)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error-700)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            This model exceeds the maximum build volume (256 x 256 x 256 mm) and cannot
            be printed. Please resize the model and re-upload.
          </div>
        )}

        {/* Quantity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Quantity
          </span>
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(MIN_QUANTITY, q - 1))}
              disabled={quantity <= MIN_QUANTITY}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity <= MIN_QUANTITY ? 'not-allowed' : 'pointer',
                opacity: quantity <= MIN_QUANTITY ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              -
            </button>
            <input
              type="number"
              min={MIN_QUANTITY}
              max={maxQuantity}
              value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  setQuantity(Math.min(maxQuantity, Math.max(MIN_QUANTITY, val)));
                }
              }}
              style={{
                width: '3.5rem',
                textAlign: 'center',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)',
              }}
            />
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              disabled={quantity >= maxQuantity}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity >= maxQuantity ? 'not-allowed' : 'pointer',
                opacity: quantity >= maxQuantity ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              +
            </button>
          </div>
          {boundingBox && maxQuantity < 100 && (
            <p
              style={{
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                lineHeight: 'var(--leading-relaxed)',
              }}
            >
              Max {maxQuantity} per build plate. For larger quantities, place additional orders.
            </p>
          )}
        </div>

        {/* Coupon Code */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Coupon Code
          </span>
          {appliedCoupon ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-success-subtle, rgba(34,197,94,0.08))',
                border: '1px solid var(--color-success-600, #16a34a)',
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-success-600, #16a34a)',
                    fontFamily: 'monospace',
                  }}
                >
                  {appliedCoupon.code}
                </span>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: '1px' }}>
                  {[
                    appliedCoupon.percentOff ? `${appliedCoupon.percentOff}% off` : null,
                    appliedCoupon.freeShipping ? 'Free shipping' : null,
                    appliedCoupon.waiveMinimum ? 'No minimum' : null,
                  ]
                    .filter(Boolean)
                    .join(' + ')}
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 'var(--space-1)',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex" style={{ gap: 'var(--space-2)' }}>
              <input
                type="text"
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value.toUpperCase());
                  setCouponError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApplyCoupon();
                }}
                placeholder="Enter code"
                style={{
                  flex: 1,
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${couponError ? 'var(--color-error-500)' : 'var(--border-default)'}`,
                  padding: 'var(--space-2) var(--space-2)',
                  minHeight: '36px',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)',
                  textTransform: 'uppercase',
                  fontFamily: 'monospace',
                }}
              />
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponInput.trim()}
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-accent)',
                  background: 'none',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2) var(--space-3)',
                  minHeight: '36px',
                  cursor: couponLoading || !couponInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: couponLoading || !couponInput.trim() ? 0.5 : 1,
                }}
              >
                {couponLoading ? '...' : 'Apply'}
              </button>
            </div>
          )}
          {couponError && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', margin: 0 }}>
              {couponError}
            </p>
          )}
        </div>

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />

        {/* Pricing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {quantity > 1 && effectiveRawPrice && (
            <PriceLine label="Unit cost" value={`$${effectiveRawPrice.toFixed(2)}`} muted />
          )}
          {baselineActive && (
            <PriceLine label="Order minimum" value={`$${MINIMUM_ORDER_PRICE.toFixed(2)}`} muted />
          )}
          <PriceLine
            label={quantity > 1 ? `Subtotal (x${quantity})` : 'Subtotal'}
            value={rawSubtotal ? `$${rawSubtotal.toFixed(2)}` : '\u2014'}
          />
          {discount > 0 && (
            <PriceLine
              label={`Coupon (${appliedCoupon?.percentOff}% off)`}
              value={`-$${discount.toFixed(2)}`}
              muted
            />
          )}
          <PriceLine
            label="Shipping"
            value={appliedCoupon?.freeShipping ? 'Free (coupon)' : 'At checkout'}
            muted
          />
          <PriceLine label="Tax" value="At checkout" muted />
        </div>

        {estimatedDays && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
            }}
          >
            Est. production: {estimatedDays} business day{estimatedDays !== 1 ? 's' : ''}
          </p>
        )}

        {/* Total */}
        {subtotal && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
            <div className="flex justify-between items-center">
              <span
                style={{
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-primary-600)',
                }}
              >
                ${subtotal.toFixed(2)}
              </span>
            </div>
          </>
        )}

        {/* Legal checkboxes -- visible once selections are complete */}
        {isComplete && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <label
                className="flex items-start"
                style={{ gap: 'var(--space-2)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span style={{ color: 'var(--text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
                  I accept the terms of service and refund policy. Printed items are
                  non-returnable unless defective.
                </span>
              </label>
              <label
                className="flex items-start"
                style={{ gap: 'var(--space-2)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={ownershipConfirmed}
                  onChange={(e) => setOwnershipConfirmed(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span style={{ color: 'var(--text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
                  I confirm I own or have the right to print this file. Printability
                  depends on the model and is not guaranteed.
                </span>
              </label>
            </div>
          </>
        )}

        {/* Checkout CTA */}
        <Button
          onClick={handleCheckout}
          disabled={!canCheckout || isLoading}
          size="lg"
          style={{ width: '100%', marginTop: 'var(--space-1)' }}
        >
          {getButtonLabel()}
        </Button>

        {error && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error-500)',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        {/* Trust line */}
        <div
          className="flex items-center justify-center"
          style={{
            gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={{ height: '0.875rem', width: '0.875rem' }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span>Secure checkout via Stripe</span>
        </div>
      </div>
    </div>
  );
};

/* ---- helpers ---- */

const SummaryRow = ({
  label,
  value,
  placeholder,
}: {
  label: string;
  value?: string;
  placeholder?: string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </span>
    {value ? (
      <span
        className="truncate"
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
        }}
      >
        {value}
      </span>
    ) : (
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-tertiary)',
          fontStyle: 'italic',
        }}
      >
        {placeholder ?? '\u2014'}
      </span>
    )}
  </div>
);

const PriceLine = ({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) => (
  <div className="flex justify-between" style={{ fontSize: 'var(--text-sm)' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span
      style={{
        fontWeight: muted ? undefined : 'var(--font-weight-medium)',
        color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)',
        fontSize: muted ? 'var(--text-xs)' : undefined,
      }}
    >
      {value}
    </span>
  </div>
);

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);
